import React, { useState } from 'react';
import { useAuth } from '../context/authContext';
import { submitFeedback } from '../services/api';

const FeedbackModal = ({ isOpen, onClose }) => {
    const { user, userProfile } = useAuth();
    const [feedback, setFeedback] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!feedback.trim()) {
            setError('Please enter some feedback');
            return;
        }

        if (!user) {
            setError('You must be logged in to submit feedback');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            const token = await user.getIdToken();
            await submitFeedback(feedback, token);
            setSubmitted(true);
            setFeedback('');
            
            // Close modal after 2 seconds
            setTimeout(() => {
                setSubmitted(false);
                onClose();
            }, 2000);
        } catch (err) {
            console.error('Error submitting feedback:', err);
            setError(err.message || 'Failed to submit feedback. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!submitting) {
            setFeedback('');
            setError('');
            setSubmitted(false);
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={handleClose}
        >
            <div 
                className="bg-neutral-0 hairline p-6 max-w-md w-full mx-4 relative"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close button */}
                <button
                    onClick={handleClose}
                    disabled={submitting}
                    className="absolute top-4 right-4 text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0"
                    aria-label="Close"
                >
                    ×
                </button>

                {submitted ? (
                    <div className="text-center py-8">
                        <div className="text-4xl mb-4">✓</div>
                        <h2 className="font-sans text-2xl font-medium text-neutral-900 mb-2">
                            Thank You!
                        </h2>
                        <p className="font-sans text-base text-neutral-900 opacity-80">
                            Your feedback has been submitted.
                        </p>
                    </div>
                ) : (
                    <>
                        <h2 className="font-sans text-2xl font-medium text-neutral-900 mb-4">
                            Share Your Feedback
                        </h2>
                        <p className="font-sans text-sm text-neutral-900 opacity-80 mb-6">
                            Have a suggestion, found a bug, or want to share your thoughts? We'd love to hear from you!
                        </p>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label 
                                    htmlFor="feedback" 
                                    className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2"
                                >
                                    FEEDBACK
                                </label>
                                <textarea
                                    id="feedback"
                                    value={feedback}
                                    onChange={(e) => setFeedback(e.target.value)}
                                    placeholder="Tell us what's on your mind..."
                                    className="w-full p-3 border border-neutral-900 bg-neutral-0 font-sans text-sm text-neutral-900 placeholder-neutral-600 focus:outline-none focus:border-neutral-900 resize-none"
                                    rows={6}
                                    disabled={submitting}
                                    required
                                />
                            </div>

                            {error && (
                                <div className="bg-red-50 p-3 hairline border-red-200">
                                    <p className="font-sans text-sm text-red-900 font-mono">
                                        {error}
                                    </p>
                                </div>
                            )}

                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    disabled={submitting}
                                    className="flex-1 px-4 py-2 font-mono text-sm uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting || !feedback.trim()}
                                    className="flex-1 px-4 py-2 font-mono text-sm uppercase tracking-wider transition-opacity duration-120 disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{ 
                                        backgroundColor: '#1b263b', 
                                        color: '#FFFFFF' 
                                    }}
                                >
                                    {submitting ? 'Submitting...' : 'Submit'}
                                </button>
                            </div>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};

export default FeedbackModal;

